import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { 
    LayoutDashboard, ShoppingCart, Package, Leaf, Target, BookOpen, 
    Paintbrush, PenTool, Users, Megaphone, BarChart3, 
    MessageSquare, Settings, ChevronDown, ChevronRight, LogOut, 
    Bell, Search, Menu, X, Store, Star, Radio
} from 'lucide-react';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import { useUIStore } from '../stores/useUIStore';
import { useRealtimeAdmin } from '../hooks/useRealtimeAdmin';
import { ADMIN_ROUTES } from '@wonderherbals/shared';
import AICoPilotWidget from './AICoPilotWidget';
import { CommandPalette } from './CommandPalette';
import { useApiQuery } from '../hooks/useApi';

// ── Sidebar Navigation Config ──────────────────────────────────

interface NavItem {
    label: string;
    icon: React.ElementType;
    path?: string;
    badge?: string;
    children?: { label: string; path: string }[];
}

const NAV_ITEMS: NavItem[] = [
    {
        label: 'Dashboard',
        icon: LayoutDashboard,
        path: ADMIN_ROUTES.DASHBOARD,
    },
    {
        label: 'Orders',
        icon: ShoppingCart,
        children: [
            { label: 'All Orders', path: ADMIN_ROUTES.ORDERS },
            { label: 'Pending', path: `${ADMIN_ROUTES.ORDERS}?status=pending` },
            { label: 'Shipped', path: `${ADMIN_ROUTES.ORDERS}?status=shipped` },
            { label: 'Logistics Command', path: ADMIN_ROUTES.LOGISTICS },
            { label: 'Returns', path: `${ADMIN_ROUTES.ORDERS}?status=returned` },
        ],
    },
    {
        label: 'Products',
        icon: Package,
        children: [
            { label: 'All Products', path: ADMIN_ROUTES.PRODUCTS },
            { label: 'Add Product', path: ADMIN_ROUTES.PRODUCT_NEW },
            { label: 'Inventory', path: `${ADMIN_ROUTES.PRODUCTS}?tab=inventory` },
            { label: 'Bulk Import', path: `${ADMIN_ROUTES.PRODUCTS}?tab=import` },
        ],
    },
    {
        label: 'Ingredients Library',
        icon: Leaf,
        children: [
            { label: 'All Ingredients', path: ADMIN_ROUTES.INGREDIENTS },
            { label: 'Add Ingredient', path: ADMIN_ROUTES.INGREDIENT_NEW },
        ],
    },
    {
        label: 'Concerns Library',
        icon: Target,
        children: [
            { label: 'All Concerns', path: ADMIN_ROUTES.CONCERNS },
            { label: 'Add Concern', path: ADMIN_ROUTES.CONCERN_NEW },
        ],
    },
    {
        label: 'Collections',
        icon: BookOpen,
        path: ADMIN_ROUTES.COLLECTIONS,
    },
    {
        label: 'Store Builder',
        icon: Paintbrush,
        children: [
            { label: 'Web Editor', path: ADMIN_ROUTES.HOMEPAGE },
            { label: 'Mobile App', path: ADMIN_ROUTES.MOBILE_APP },
            { label: 'Pages', path: ADMIN_ROUTES.PAGES },
        ],
    },
    {
        label: 'Content',
        icon: PenTool,
        children: [
            { label: 'Blogs', path: ADMIN_ROUTES.BLOGS },
            { label: 'Instagram Studio', path: ADMIN_ROUTES.INSTAGRAM },
            { label: 'Videos', path: `${ADMIN_ROUTES.BLOGS}?tab=videos` },
            { label: 'FAQs', path: `${ADMIN_ROUTES.BLOGS}?tab=faqs` },
        ],
    },
    {
        label: 'Customers',
        icon: Users,
        children: [
            { label: 'All Customers', path: ADMIN_ROUTES.CUSTOMERS },
            { label: 'Segments', path: `${ADMIN_ROUTES.CUSTOMERS}?tab=segments` },
            { label: 'Import from Shopify', path: ADMIN_ROUTES.IMPORT_SHOPIFY },
        ],
    },
    {
        label: 'Marketing',
        icon: Megaphone,
        children: [
            { label: 'Coupons', path: ADMIN_ROUTES.COUPONS },
            { label: 'Meta Hub', path: ADMIN_ROUTES.META_HUB },
            { label: 'WhatsApp Hub', path: ADMIN_ROUTES.WHATSAPP_HUB },
            { label: 'WhatsApp Brain', path: ADMIN_ROUTES.WHATSAPP_BRAIN },
            { label: 'SEO Command Centre', path: ADMIN_ROUTES.SEO_DASHBOARD },
            { label: 'Follow-up Automation', path: ADMIN_ROUTES.FOLLOWUPS },
        ],
    },
    {
        label: 'Analytics',
        icon: BarChart3,
        path: ADMIN_ROUTES.ANALYTICS,
    },
    {
        label: 'Inbox',
        icon: MessageSquare,
        path: ADMIN_ROUTES.CHAT,
        badge: '3',
    },
    {
        label: 'Reviews',
        icon: Star,
        path: ADMIN_ROUTES.REVIEWS,
    },
    {
        label: 'Team',
        icon: Users,
        children: [
            { label: 'Tasks', path: ADMIN_ROUTES.TASKS },
            { label: 'Staff & Payroll', path: ADMIN_ROUTES.STAFF },
        ],
    },
    {
        label: 'Settings',
        icon: Settings,
        path: ADMIN_ROUTES.SETTINGS,
    },
];

// ── Breadcrumb helper ──────────────────────────────────────────

function getBreadcrumbs(pathname: string): { label: string; path: string }[] {
    const crumbs: { label: string; path: string }[] = [{ label: 'Home', path: ADMIN_ROUTES.DASHBOARD }];

    const pathMap: Record<string, string> = {
        [ADMIN_ROUTES.ORDERS]: 'Orders',
        [ADMIN_ROUTES.PRODUCTS]: 'Products',
        [ADMIN_ROUTES.PRODUCT_NEW]: 'Add Product',
        [ADMIN_ROUTES.INGREDIENTS]: 'Ingredients',
        [ADMIN_ROUTES.INGREDIENT_NEW]: 'Add Ingredient',
        [ADMIN_ROUTES.CONCERNS]: 'Concerns',
        [ADMIN_ROUTES.CONCERN_NEW]: 'Add Concern',
        [ADMIN_ROUTES.COLLECTIONS]: 'Collections',
        [ADMIN_ROUTES.HOMEPAGE]: 'Store Builder',
        [ADMIN_ROUTES.PAGES]: 'Page Builder',
        [ADMIN_ROUTES.BLOGS]: 'Content',
        [ADMIN_ROUTES.CUSTOMERS]: 'Customers',
        [ADMIN_ROUTES.COUPONS]: 'Marketing',
        [ADMIN_ROUTES.ANALYTICS]: 'Analytics',
        [ADMIN_ROUTES.CHAT]: 'Inbox',
        [ADMIN_ROUTES.REVIEWS]: 'Reviews',
        [ADMIN_ROUTES.SETTINGS]: 'Settings',
    };

    // Direct match
    if (pathMap[pathname]) {
        crumbs.push({ label: pathMap[pathname], path: pathname });
        return crumbs;
    }

    // Dynamic routes
    const segments = pathname.split('/').filter(Boolean);
    let currentPath = '';
    for (const segment of segments) {
        currentPath += `/${segment}`;
        const label = pathMap[currentPath] || segment;
        crumbs.push({ label, path: currentPath });
    }

    return crumbs;
}

// ── Sidebar Item ───────────────────────────────────────────────

function SidebarNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    const isActive = item.path
        ? location.pathname === item.path
        : item.children?.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path.split('?')[0]));

    const hasChildren = item.children && item.children.length > 0;

    if (collapsed) {
        return (
            <Link
                to={item.path || item.children?.[0]?.path || '#'}
                className={`flex items-center justify-center p-2.5 rounded-lg transition-colors ${isActive
                        ? 'bg-admin-sidebar-hover text-white'
                        : 'text-admin-sidebar-text hover:bg-admin-sidebar-hover hover:text-white'
                    }`}
                title={item.label}
            >
                <item.icon className="w-5 h-5" />
                {item.badge && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-status-error text-white text-2xs rounded-full flex items-center justify-center">
                        {item.badge}
                    </span>
                )}
            </Link>
        );
    }

    return (
        <div>
            {hasChildren ? (
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full sidebar-item ${isActive ? 'text-white' : ''}`}
                >
                    <item.icon className="w-5 h-5 shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            ) : (
                <Link
                    to={item.path!}
                    className={`w-full ${isActive ? 'sidebar-item-active' : 'sidebar-item'}`}
                >
                    <item.icon className="w-5 h-5 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                        <span className="px-1.5 py-0.5 bg-status-error text-white text-2xs rounded-full">
                            {item.badge}
                        </span>
                    )}
                </Link>
            )}

            {hasChildren && isOpen && (
                <div className="mt-1 space-y-0.5">
                    {item.children!.map((child) => {
                        const childActive = location.pathname + location.search === child.path ||
                            location.pathname === child.path.split('?')[0];
                        return (
                            <Link
                                key={child.path}
                                to={child.path}
                                className={childActive ? 'sidebar-subitem-active' : 'sidebar-subitem'}
                            >
                                {child.label}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Main Layout ────────────────────────────────────────────────

export default function AdminLayout() {
    const { supabaseUser, isAdmin, isInitialized, isLoading, signOut } = useAdminAuthStore();
    const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

    // Initialize Supabase Realtime event subscriptions & sound engine
    useRealtimeAdmin();

    // Global keyboard listener for Ctrl+K / Cmd+K
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setCommandPaletteOpen((prev) => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const breadcrumbs = getBreadcrumbs(location.pathname);

    // Auth guard
    if (!isInitialized || isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-admin-bg">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-admin-forest" />
            </div>
        );
    }

    if (!supabaseUser || !isAdmin) {
        return <Navigate to={ADMIN_ROUTES.LOGIN} replace />;
    }

    return (
        <div className="flex h-screen overflow-hidden bg-admin-bg font-sans antialiased">
            {/* Mobile overlay */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
          fixed lg:static inset-y-0 left-0 z-50
          bg-admin-sidebar flex flex-col transition-all duration-300
          ${sidebarCollapsed ? 'w-[68px]' : 'w-[240px]'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
            >
                {/* Logo */}
                <div className={`flex items-center h-16 px-4 border-b border-white/10 ${sidebarCollapsed ? 'justify-center' : ''}`}>
                    <Link to={ADMIN_ROUTES.DASHBOARD} className="flex items-center gap-2">
                        <Store className="w-7 h-7 text-admin-gold shrink-0" />
                        {!sidebarCollapsed && (
                            <div>
                                <h1 className="text-white font-semibold text-sm leading-tight">Wonder Herbals</h1>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    <p className="text-emerald-400 font-bold text-[10px] uppercase tracking-wider">Realtime Live</p>
                                </div>
                            </div>
                        )}
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto sidebar-scroll py-4 px-3 space-y-1">
                    {NAV_ITEMS.map((item) => (
                        <SidebarNavItem key={item.label} item={item} collapsed={sidebarCollapsed} />
                    ))}
                </nav>

                {/* Collapse toggle */}
                <div className="p-3 border-t border-white/10">
                    <button
                        type="button"
                        onClick={toggleSidebarCollapsed}
                        className="sidebar-item w-full justify-center"
                    >
                        {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {!sidebarCollapsed && <span className="text-xs">Collapse</span>}
                    </button>
                </div>
            </aside>

            {/* Main content area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top header */}
                <header className="h-16 bg-white border-b border-admin-border flex items-center justify-between px-4 lg:px-6 shrink-0 shadow-xs">
                    <div className="flex items-center gap-3">
                        {/* Mobile menu toggle */}
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100"
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>

                        {/* Breadcrumbs */}
                        <nav className="flex items-center gap-1.5 text-sm">
                            {breadcrumbs.map((crumb, i) => (
                                <span key={crumb.path} className="flex items-center gap-1.5">
                                    {i > 0 && <ChevronRight className="w-3 h-3 text-admin-text-muted" />}
                                    {i === breadcrumbs.length - 1 ? (
                                        <span className="font-medium text-admin-text">{crumb.label}</span>
                                    ) : (
                                        <Link to={crumb.path} className="text-admin-text-muted hover:text-admin-text transition-colors">
                                            {crumb.label}
                                        </Link>
                                    )}
                                </span>
                            ))}
                        </nav>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Global Search / Command Palette Trigger */}
                        <button
                            type="button"
                            onClick={() => setCommandPaletteOpen(true)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200/80 text-admin-text-muted transition-all border border-gray-200/60"
                        >
                            <Search className="w-4 h-4" />
                            <span className="text-xs font-medium hidden sm:inline-block">Quick search...</span>
                            <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono font-bold text-gray-500 bg-white border border-gray-300 rounded shadow-xs">
                                ⌘K
                            </kbd>
                        </button>

                        {/* Realtime Live Status Indicator */}
                        <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100 text-2xs font-bold uppercase tracking-wider">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Live Sync
                        </div>

                        {/* User menu */}
                        <div className="flex items-center gap-2 pl-3 border-l border-admin-border">
                            <div className="w-8 h-8 rounded-full bg-admin-forest text-white flex items-center justify-center text-xs font-semibold shadow-xs">
                                {(supabaseUser as any)?.displayName?.[0] || supabaseUser?.email?.[0] || 'A'}
                            </div>
                            <div className="hidden sm:block">
                                <p className="text-sm font-medium text-admin-text leading-tight">
                                    {(supabaseUser as {user_metadata?: {display_name?: string}}).user_metadata?.display_name || supabaseUser.email?.split('@')[0] || 'Admin'}
                                </p>
                                <p className="text-2xs text-admin-text-muted">Administrator</p>
                            </div>
                            <button
                                type="button"
                                onClick={signOut}
                                className="p-1.5 rounded-lg hover:bg-gray-100 text-admin-text-muted"
                                title="Sign out"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto p-4 lg:p-6 bg-admin-bg">
                    <SetupBanner />
                    <Outlet />
                </main>
            </div>
            
            {/* Global Modals & Widgets */}
            <AICoPilotWidget />
            <CommandPalette
                isOpen={commandPaletteOpen}
                onClose={() => setCommandPaletteOpen(false)}
            />
        </div>
    );
}


/** Persistent notice while the database is missing migrations the admin relies on. */
function SetupBanner() {
    const { data } = useApiQuery<any>(['admin', 'system-health'], '/admin/system/health', { staleTime: 10 * 60 * 1000, retry: 0 });
    const location = useLocation();
    if (!data?.migrationRequired || location.search.includes('tab=system')) return null;
    return (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 print:hidden">
            <span className="font-semibold">Setup incomplete:</span>
            <span>database migration 009 is not applied, so stock history, order timelines and settings are not being saved.</span>
            <Link to={`${ADMIN_ROUTES.SETTINGS}?tab=system`} className="ml-auto font-semibold underline">Fix in System health</Link>
        </div>
    );
}
